/**
 * Dates civiles en arithmétique ENTIÈRE pure — aucune dépendance, aucun objet
 * Date JavaScript, aucun fuseau horaire.
 *
 * Justification (ADR-001, NFR-STK-002) : le moteur travaille sur des dates
 * « murales » légales (Europe/Brussels implicite, comme la v1 sur SQL Server
 * `datetime`). Représenter les jours par des entiers (jours époque civile,
 * dateInt AAAAMMJJ) élimine toute classe de bugs DST/UTC et garantit le
 * déterminisme exigé par INV-005 et le golden master (chap. 18).
 *
 * Algorithmes civil↔jours : Howard Hinnant, « chrono-Compatible Low-Level
 * Date Algorithms » (domaine public), exacts sur tout le calendrier grégorien
 * proleptique.
 */
export function dateInt(d) {
    return d.an * 10000 + d.mois * 100 + d.jour;
}
export function depuisDateInt(di) {
    const an = Math.trunc(di / 10000);
    const mois = Math.trunc((di % 10000) / 100);
    const jour = di % 100;
    return { an, mois, jour };
}
/** Division euclidienne au sol (JS `%` est tronqué, pas au sol). */
function divFloor(a, b) {
    return Math.floor(a / b);
}
function modFloor(a, b) {
    return ((a % b) + b) % b;
}
/**
 * Nombre de jours depuis l'époque civile 1970-01-01 (jour 0 = jeudi).
 * Hinnant `days_from_civil`.
 */
export function joursDepuisEpoque(d) {
    let { an } = d;
    const { mois, jour } = d;
    an -= mois <= 2 ? 1 : 0;
    const ere = divFloor(an, 400);
    const adl = an - ere * 400; // année de l'ère [0, 399]
    const jdl = divFloor(153 * (mois + (mois > 2 ? -3 : 9)) + 2, 5) + jour - 1; // jour de l'année [0, 365]
    const jde = adl * 365 + divFloor(adl, 4) - divFloor(adl, 100) + jdl; // jour de l'ère
    return ere * 146097 + jde - 719468;
}
/** Hinnant `civil_from_days` — inverse exact de {@link joursDepuisEpoque}. */
export function depuisJoursEpoque(j) {
    const z = j + 719468;
    const ere = divFloor(z, 146097);
    const jde = z - ere * 146097; // [0, 146096]
    const adl = divFloor(jde - divFloor(jde, 1460) + divFloor(jde, 36524) - divFloor(jde, 146096), 365);
    const an = adl + ere * 400;
    const jdl = jde - (365 * adl + divFloor(adl, 4) - divFloor(adl, 100)); // [0, 365]
    const mp = divFloor(5 * jdl + 2, 153); // [0, 11]
    const jour = jdl - divFloor(153 * mp + 2, 5) + 1; // [1, 31]
    const mois = mp < 10 ? mp + 3 : mp - 9; // [1, 12]
    return { an: an + (mois <= 2 ? 1 : 0), mois, jour };
}
/**
 * Jour de semaine ISO : 1 = lundi … 7 = dimanche (convention du code source v1, §0.2).
 * 1970-01-01 est un jeudi (4).
 */
export function jourSemaineIso(d) {
    return modFloor(joursDepuisEpoque(d) + 3, 7) + 1;
}
export function ajouterJours(d, n) {
    return depuisJoursEpoque(joursDepuisEpoque(d) + n);
}
/** Comparaison chronologique : négatif si a < b, 0 si égal, positif si a > b. */
export function comparerDates(a, b) {
    return joursDepuisEpoque(a) - joursDepuisEpoque(b);
}
export function memesDates(a, b) {
    return a.an === b.an && a.mois === b.mois && a.jour === b.jour;
}
/** Lundi de la semaine ISO contenant `d` (semaines lundi→dimanche, §2 « Période de référence »). */
export function lundiDeLaSemaine(d) {
    return ajouterJours(d, -(jourSemaineIso(d) - 1));
}
/** Nombre de jours calendaires entre deux dates (b − a). */
export function differenceJours(a, b) {
    return joursDepuisEpoque(b) - joursDepuisEpoque(a);
}
/**
 * Numéro de semaine ISO 8601 (colonne « Semaine » de la table Compteurs, §11.14).
 * La semaine 1 est celle qui contient le premier jeudi de l'année.
 */
export function semaineIso(d) {
    const jeudi = ajouterJours(d, 4 - jourSemaineIso(d));
    const premierJanvier = { an: jeudi.an, mois: 1, jour: 1 };
    return divFloor(differenceJours(premierJanvier, jeudi), 7) + 1;
}
export function instant(d, heures, minutes, secondes = 0) {
    return { jourEpoque: joursDepuisEpoque(d), secondes: heures * 3600 + minutes * 60 + secondes };
}
export function secondesAbsolues(i) {
    return i.jourEpoque * 86400 + i.secondes;
}
export function depuisSecondesAbsolues(s) {
    const jourEpoque = divFloor(s, 86400);
    return { jourEpoque, secondes: s - jourEpoque * 86400 };
}
/** RG-TMP-001 — durée en minutes entières entre deux instants (différence début→fin). */
/** `DATEDIFF(minute, début, fin)` SQL Server : nombre de frontières de minute franchies (23:59:59 → 02:00:00 = 121). */
export function dureeMinutes(debut, fin) {
    return Math.floor(secondesAbsolues(fin) / 60) - Math.floor(secondesAbsolues(debut) / 60);
}
/** Dernier jour du mois (gère les années bissextiles). */
export function dernierJourDuMois(an, mois) {
    const premierDuSuivant = mois === 12 ? { an: an + 1, mois: 1, jour: 1 } : { an, mois: mois + 1, jour: 1 };
    return ajouterJours(premierDuSuivant, -1);
}
//# sourceMappingURL=date-civile.js.map